import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { Spin, Modal, message } from 'antd';
import * as Icons from '@ant-design/icons';
import styled from 'styled-components';
import { useService } from '@core/inversify-react';
import BarcodeScan from '../../../../components/Common/BarcodeScan';
import { useBranchInspectionById } from '../hooks';
import { GetScansRepoType, IGetScansRepo, ScanTrackingCodeRepoType, IScanTrackingCodeRepo, ChangeInspectionStatusRepoType, IChangeInspectionStatusRepo } from '../repos';
import { IScanItem } from '../interfaces';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f5f5f5;
`;

const Header = styled.div`
  background-color: #fff;
  border-bottom: 1px solid #e8e8e8;
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  margin: 0;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`;

const Button = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s;
  border: ${(props) => (props.$primary || props.$danger ? 'none' : '1px solid #d9d9d9')};
  background-color: ${(props) => (props.$primary ? '#1890ff' : props.$danger ? '#ff4d4f' : '#fff')};
  color: ${(props) => (props.$primary || props.$danger ? '#fff' : '#262626')};

  &:hover {
    background-color: ${(props) => (props.$primary ? '#40a9ff' : props.$danger ? '#ff7875' : '#fafafa')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  display: flex;
  height: calc(100vh - 73px);

  @media (max-width: 768px) {
    flex-direction: column;
    height: auto;
  }
`;

const LeftPanel = styled.div`
  width: 50%;
  padding: 24px;
  overflow: auto;

  @media (max-width: 768px) {
    width: 100%;
    overflow: visible;
  }
`;

const RightPanel = styled.div`
  width: 50%;
  background-color: #fff;
  border-left: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
    border-left: none;
    border-top: 1px solid #e8e8e8;
    min-height: 500px;
  }
`;

const Card = styled.div`
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  padding: 24px;
  margin-bottom: 24px;
`;

const BarcodeContainer = styled.div`
  display: flex;
  gap: 32px;
  margin-bottom: 24px;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 16px;
  }
`;

const BarcodeIcon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const InfoSection = styled.div`
  flex: 1;
`;

const InfoTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoIcon = styled.div<{ $color?: string }>`
  flex-shrink: 0;
  margin-top: 2px;
  color: ${(props) => props.$color || '#8c8c8c'};
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.p`
  font-size: 12px;
  color: #8c8c8c;
  margin: 0 0 2px 0;
`;

const InfoValue = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: #262626;
  margin: 0;
`;

const InfoExtra = styled.p`
  font-size: 12px;
  color: ${(props) => props.color || '#8c8c8c'};
  margin: 4px 0 0 0;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s;

  &:focus {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    outline: none;
  }
`;

const LastScanned = styled.div`
  margin-top: 12px;
  padding: 12px;
  background-color: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const LastScannedLabel = styled.span`
  font-size: 12px;
  color: #8c8c8c;
`;

const LastScannedValue = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 14px;
  font-weight: 600;
  color: #262626;
`;

const StatCard = styled(Card)`
  padding: 20px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 16px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const StatLabel = styled.p`
  font-size: 12px;
  color: #8c8c8c;
  margin: 0 0 4px 0;
`;

const StatValue = styled.p<{ $color?: string }>`
  font-size: 32px;
  font-weight: 600;
  color: ${(props) => props.$color || '#262626'};
  margin: 0;
`;

const StatIcon = styled.div<{ $bgColor?: string; $color?: string }>`
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: ${(props) => props.$bgColor || '#f0f0f0'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.$color || '#8c8c8c'};
  font-size: 24px;
`;

const Note = styled.div`
  padding: 16px;
  background-color: #e6f7ff;
  border: 1px solid #91d5ff;
  border-radius: 6px;

  p {
    margin: 0;
    font-size: 14px;
    color: #0050b3;

    strong {
      font-weight: 600;
    }
  }
`;

const RightHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e8e8e8;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
`;

const RightTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #262626;
  margin: 0;
`;

const RightSubtitle = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin: 0;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px;
`;

const EmptyIcon = styled.div`
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background-color: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  font-size: 48px;
  color: #bfbfbf;
`;

const EmptyTitle = styled.p`
  font-size: 16px;
  color: #595959;
  margin: 0 0 8px 0;
`;

const EmptySubtitle = styled.p`
  font-size: 14px;
  color: #bfbfbf;
  margin: 0;
`;

const ScanList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const ScanItem = styled.div<{ $status: 'pending' | 'success' | 'error' }>`
  padding: 9px;
  border-radius: 6px;
  border: 2px solid ${(props) => (props.$status === 'success' ? '#b7eb8f' : props.$status === 'error' ? '#ffa39e' : '#d9d9d9')};
  background-color: ${(props) => (props.$status === 'success' ? '#f6ffed' : props.$status === 'error' ? '#fff1f0' : '#fafafa')};
  margin-bottom: 8px;
  transition: all 0.3s;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ScanItemContent = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`;

const ScanItemLeft = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const ScanNumber = styled.div<{ $status: 'pending' | 'success' | 'error' }>`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  background-color: ${(props) => (props.$status === 'success' ? '#b7eb8f' : props.$status === 'error' ? '#ffa39e' : '#d9d9d9')};
  color: ${(props) => (props.$status === 'success' ? '#389e0d' : props.$status === 'error' ? '#cf1322' : '#595959')};
`;

const ScanInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ScanCode = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
`;

const ScanCodeText = styled.p`
  font-family: 'Courier New', monospace;
  font-size: 20px;
  font-weight: 700;
  color: #262626;
  margin: 0;
  word-break: break-all;
`;

const ScanTime = styled.p`
  font-size: 11px;
  color: #8c8c8c;
  margin: 0;
`;

const ScanError = styled.div`
  margin-top: 6px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 6px;
  background-color: #ffccc7;
  border-radius: 4px;
`;

const ScanErrorText = styled.p`
  font-size: 11px;
  color: #a8071a;
  margin: 0;
  flex: 1;
`;

const ScanTrackCode = styled.div`
  flex-shrink: 0;
  text-align: right;
`;

const ScanTrackCodeText = styled.p`
  font-family: 'Courier New', monospace;
  font-size: 13px;
  font-weight: 500;
  color: #1890ff;
  margin: 0;
`;

const ScanFooter = styled.div`
  padding: 16px 24px;
  border-top: 1px solid #e8e8e8;
  background-color: #fafafa;
`;

const ScanFooterContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
`;

const ScanFooterLabel = styled.span`
  color: #595959;
`;

const ScanFooterValue = styled.span`
  font-weight: 600;
  color: #262626;
`;

const RetryButton = styled.button`
  margin-top: 6px;
  padding: 3px 6px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background-color: #fff;
  font-size: 11px;
  color: #595959;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #40a9ff;
    color: #40a9ff;
  }
`;

export const BranchInspectionScanPage = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [scanData, setScanData] = useState<IScanItem[]>([]);
  const [lastScanned, setLastScanned] = useState('');
  const [initialLoading, setInitialLoading] = useState(true);
  const [scanQueue, setScanQueue] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const { data: inspectionData, isFetching: inspectionLoading } = useBranchInspectionById(id);
  const getScansRepo = useService<IGetScansRepo>(GetScansRepoType);
  const scanTrackingCodeRepo = useService<IScanTrackingCodeRepo>(ScanTrackingCodeRepoType);
  const changeInspectionStatusRepo = useService<IChangeInspectionStatusRepo>(ChangeInspectionStatusRepoType);

  // Scroll to top when new scan added
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [scanData]);

  // Load initial scans
  useEffect(() => {
    const loadScans = async () => {
      setInitialLoading(true);
      const result = await getScansRepo.execute(id);

      if (result.status === 200) {
        const scans: IScanItem[] = result.response.map((scan) => ({
          ...scan,
          status: 'success' as const,
        }));
        setScanData(scans);
      } else {
        message.error(result.response);
      }
      setInitialLoading(false);
    };

    loadScans();
  }, [id, getScansRepo]);

  // Process scan queue
  useEffect(() => {
    const processScanQueue = async () => {
      if (scanQueue.length === 0 || isProcessing) return;

      setIsProcessing(true);
      const currentTrackCode = scanQueue[0];
      const tempId = Date.now();

      const newScan: IScanItem = {
        id: tempId,
        inspectionId: parseInt(id),
        declarationId: 0,
        trackCode: currentTrackCode,
        trendyol: 0,
        barcode: currentTrackCode,
        partnerId: null,
        scannedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        status: 'pending',
      };

      // Add to list with pending status
      setScanData((prev) => [newScan, ...prev]);

      // Send API request
      const result = await scanTrackingCodeRepo.execute(id, { track_code: currentTrackCode });

      // Update scan status
      setScanData((prev) =>
        prev.map((item) => {
          if (item.id === tempId) {
            if (result.status === 200) {
              return {
                ...result.response,
                status: 'success' as const,
              };
            } else {
              return {
                ...item,
                status: 'error' as const,
                errorMessage: result.response,
              };
            }
          }
          return item;
        }),
      );

      // Remove from queue
      setScanQueue((prev) => prev.slice(1));
      setIsProcessing(false);
    };

    processScanQueue();
  }, [scanQueue, isProcessing, id, scanTrackingCodeRepo]);

  const handleScan = useCallback(() => {
    const value = inputRef.current?.value.trim() ?? '';
    if (!value) return;

    setScanQueue((prev) => [...prev, value]);
    setLastScanned(value);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleScan();
    }
  };

  const handleReset = () => {
    Modal.confirm({
      title: 'Diqqət',
      content: 'Bütün scan məlumatları silinəcək və proses yenidən başlayacaq. Əminsiniz?',
      okText: 'Bəli',
      cancelText: 'Xeyr',
      onOk: () => {
        setScanData([]);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setLastScanned('');
        message.success('Məlumatlar sıfırlandı');
      },
    });
  };

  const handleFinish = () => {
    if (scanData.length === 0) {
      message.warning('Heç bir bağlama scan olunmayıb!');
      return;
    }

    Modal.confirm({
      title: 'Yoxlanışı bitirmək',
      content: `Yoxlanış tamamlanacaq. Cəmi ${scanData.length} bağlama scan olundu. Əminsiniz?`,
      okText: 'Bitir',
      cancelText: 'Ləğv et',
      onOk: async () => {
        const result = await changeInspectionStatusRepo.execute(id, { state_id: 157 });

        if (result.status === 200) {
          message.success('Yoxlanış tamamlandı!');
          history.goBack();
        } else {
          message.error(result.response);
        }
      },
    });
  };

  const handleBack = () => {
    history.goBack();
  };

  const handleRetry = useCallback((scan: IScanItem) => {
    // Remove the old error item
    setScanData((prev) => prev.filter((item) => item.id !== scan.id));

    // Add to queue for retry
    setScanQueue((prev) => [...prev, scan.trackCode?.toString() || '']);
  }, []);

  const successCount = scanData.filter((item) => item.status === 'success').length;
  const errorCount = scanData.filter((item) => item.status === 'error').length;

  const getDisplayCode = (scan: IScanItem) => {
    if (scan.trendyol && scan.trendyol > 0 && scan.barcode) {
      return scan.barcode;
    }
    return scan.trackCode?.toString() || '';
  };

  const canScan = inspectionData?.stateId === 155 || inspectionData?.stateId === 156;

  if (initialLoading || inspectionLoading) {
    return (
      <PageContainer>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
          <Spin size='large' />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <Button onClick={handleBack}>
            <Icons.ArrowLeftOutlined />
          </Button>
          <HeaderTitle>
            #{inspectionData?.id} - {inspectionData?.branch.name}
          </HeaderTitle>
        </HeaderLeft>
        <HeaderButtons>
          <Button onClick={handleReset} disabled={scanData.length === 0}>
            <Icons.ReloadOutlined />
            Sıfırla
          </Button>
          <Button $primary onClick={handleFinish}>
            <Icons.CheckCircleOutlined />
            Yoxlanışı bitir
          </Button>
        </HeaderButtons>
      </Header>

      <Content>
        <LeftPanel>
          <Card>
            <BarcodeContainer>
              <BarcodeIcon>
                <BarcodeScan barcode={lastScanned || 'Scan et'} />
              </BarcodeIcon>

              <InfoSection>
                <InfoTitle>Yoxlanış məlumatları</InfoTitle>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '12px' }}>
                  <InfoItem style={{ marginBottom: 0 }}>
                    <InfoIcon $color='#fa8c16'>
                      <Icons.ClockCircleOutlined style={{ fontSize: 16 }} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Son tarix</InfoLabel>
                      <InfoValue>{inspectionData?.deadline.value}</InfoValue>
                      <InfoExtra color='#fa8c16'>{inspectionData?.deadline.left} qalıb</InfoExtra>
                    </InfoContent>
                  </InfoItem>
                  <InfoItem style={{ marginBottom: 0 }}>
                    <InfoIcon $color='#1890ff'>
                      <Icons.CalendarOutlined style={{ fontSize: 16 }} />
                    </InfoIcon>
                    <InfoContent>
                      <InfoLabel>Yaradılıb</InfoLabel>
                      <InfoValue>{inspectionData?.createdAt}</InfoValue>
                    </InfoContent>
                  </InfoItem>
                </div>
                <InfoItem>
                  <InfoIcon $color='#595959'>
                    <Icons.FileTextOutlined style={{ fontSize: 16 }} />
                  </InfoIcon>
                  <InfoContent>
                    <InfoLabel>Qeyd</InfoLabel>
                    <InfoValue>{inspectionData?.note || 'Qeyd olunmayıb'}</InfoValue>
                  </InfoContent>
                </InfoItem>
              </InfoSection>
            </BarcodeContainer>

            <div>
              <Input
                ref={inputRef}
                type='text'
                onKeyPress={handleKeyPress}
                placeholder={canScan ? 'İzləmə kodunu daxil edin və ya scan edin...' : 'Yoxlanış statusu düzgün deyil'}
                autoFocus
                disabled={!canScan}
              />
              <Button $primary onClick={handleScan} disabled={!canScan} style={{ width: '100%', marginTop: 12, justifyContent: 'center' }}>
                Scan et
              </Button>

              {!canScan && (
                <div style={{ marginTop: 12, padding: 12, backgroundColor: '#fff1f0', border: '1px solid #ffa39e', borderRadius: 6 }}>
                  <p style={{ margin: 0, fontSize: 14, color: '#cf1322' }}>
                    <Icons.ExclamationCircleOutlined style={{ marginRight: 8 }} />
                    Yoxlanış statusu düzgün deyil. Yalnız state_id 155 və ya 156 olan yoxlanışlarda scan edilə bilər.
                  </p>
                </div>
              )}

              {lastScanned && (
                <LastScanned>
                  <LastScannedLabel>Son oxudulan:</LastScannedLabel>
                  <LastScannedValue>{lastScanned}</LastScannedValue>
                </LastScanned>
              )}
            </div>
          </Card>

          <StatCard>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <StatLabel>Cəmi scan olunub</StatLabel>
                <StatValue>{scanData.length}</StatValue>
              </div>
              <StatIcon $bgColor='#e6f7ff' $color='#1890ff'>
                <Icons.InboxOutlined />
              </StatIcon>
            </div>
          </StatCard>

          <StatGrid>
            <StatCard>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <StatLabel>Uğurlu</StatLabel>
                  <StatValue $color='#52c41a'>{successCount}</StatValue>
                </div>
                <StatIcon $bgColor='#f6ffed' $color='#52c41a'>
                  <Icons.CheckCircleOutlined />
                </StatIcon>
              </div>
            </StatCard>

            <StatCard>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <StatLabel>Xəta</StatLabel>
                  <StatValue $color='#ff4d4f'>{errorCount}</StatValue>
                </div>
                <StatIcon $bgColor='#fff1f0' $color='#ff4d4f'>
                  <Icons.CloseCircleOutlined />
                </StatIcon>
              </div>
            </StatCard>
          </StatGrid>

          <Note>
            <p>
              <strong>Qeyd:</strong> Bağlamaları scan edərkən yalnız Findex və ya United-ə məxsus barkodları oxuya bilərsiz. Oxunmayan barkodları əl ilə yuxarıdakı inputa daxil edib, enter düyməsini
              sıxmaqla əlavə edə bilərsiz.
            </p>
          </Note>
        </LeftPanel>

        <RightPanel>
          <RightHeader>
            <RightTitle>Scan olunmuş bağlamalar</RightTitle>
            <RightSubtitle>Ən yeni bağlamalar yuxarıda göstərilir</RightSubtitle>
          </RightHeader>

          {scanData.length === 0 ? (
            <EmptyState>
              <EmptyIcon>
                <Icons.InboxOutlined />
              </EmptyIcon>
              <EmptyTitle>Hələ heç bir bağlama scan olunmayıb</EmptyTitle>
              <EmptySubtitle>Bağlamaları scan etməyə başlayın</EmptySubtitle>
            </EmptyState>
          ) : (
            <ScanList ref={scrollRef}>
              {scanData.map((item) => (
                <ScanItem key={item.id} $status={item.status}>
                  <ScanItemContent>
                    <ScanItemLeft>
                      <ScanNumber $status={item.status}>
                        {item.status === 'success' && <Icons.CheckCircleOutlined style={{ fontSize: 16 }} />}
                        {item.status === 'error' && <Icons.CloseCircleOutlined style={{ fontSize: 16 }} />}
                        {item.status === 'pending' && <Icons.LoadingOutlined style={{ fontSize: 16 }} />}
                      </ScanNumber>
                      <ScanInfo>
                        <ScanCode>
                          <ScanCodeText>{getDisplayCode(item)}</ScanCodeText>
                        </ScanCode>
                        <ScanTime>{new Date(item.scannedAt).toLocaleTimeString('az-AZ')}</ScanTime>
                        {item.status === 'error' && item.errorMessage && (
                          <ScanError>
                            <Icons.ExclamationCircleOutlined style={{ fontSize: 16, color: '#a8071a' }} />
                            <ScanErrorText>{item.errorMessage}</ScanErrorText>
                          </ScanError>
                        )}
                      </ScanInfo>
                    </ScanItemLeft>
                    {item.status === 'success' && (
                      <ScanTrackCode>
                        <ScanTrackCodeText>{item.trackCode?.toString() || ''}</ScanTrackCodeText>
                      </ScanTrackCode>
                    )}
                    {item.status === 'error' && (
                      <ScanTrackCode>
                        <RetryButton onClick={() => handleRetry(item)}>
                          <Icons.ReloadOutlined /> Yenidən yoxla
                        </RetryButton>
                      </ScanTrackCode>
                    )}
                  </ScanItemContent>
                </ScanItem>
              ))}
            </ScanList>
          )}

          {scanData.length > 0 && (
            <ScanFooter>
              <ScanFooterContent>
                <ScanFooterLabel>Scroll edin və daha çox bağlama scan edin</ScanFooterLabel>
                <ScanFooterValue>Cəmi: {scanData.length}</ScanFooterValue>
              </ScanFooterContent>
            </ScanFooter>
          )}
        </RightPanel>
      </Content>
    </PageContainer>
  );
};
